import { AlertTriangle } from "lucide-react";
import type { AirbnbLabelMapping } from "@/lib/airbnbRoomMapping";
import { findDuplicatePropertyAssignments } from "@/lib/airbnbRoomMapping";

interface AirbnbRoomMappingStepProps {
  mappings: AirbnbLabelMapping[];
  propertyRoomNames: string[];
  onChange: (mappings: AirbnbLabelMapping[]) => void;
  disabled?: boolean;
}

export function AirbnbRoomMappingStep({
  mappings,
  propertyRoomNames,
  onChange,
  disabled = false,
}: AirbnbRoomMappingStepProps) {
  const duplicateAssignments = findDuplicatePropertyAssignments(mappings);
  const listingCount = mappings.length;
  const propertyCount = propertyRoomNames.length;

  const setMapping = (detectedLabel: string, propertyRoomName: string | null) => {
    onChange(
      mappings.map((m) =>
        m.detectedLabel === detectedLabel ? { ...m, propertyRoomName: propertyRoomName || null } : m
      )
    );
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Airbnb&apos;s photo tour lists{" "}
        <span className="font-medium text-foreground">
          {listingCount} room{listingCount !== 1 ? "s" : ""}
        </span>
        ; your checklist has{" "}
        <span className="font-medium text-foreground">
          {propertyCount} room{propertyCount !== 1 ? "s" : ""}
        </span>
        . Match each listing room to the right room in your app. If two listing names are the same
        space, assign them to one room — otherwise pick different rooms.
      </p>

      {duplicateAssignments.length > 0 && (
        <div className="flex gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-sm">
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" />
          <div className="space-y-1">
            {duplicateAssignments.map(({ propertyRoomName, labels }) => (
              <p key={propertyRoomName}>
                <span className="font-medium">{labels.join('", "')}</span> are both assigned to{" "}
                <span className="font-medium">{propertyRoomName}</span>. Change one if they are
                different rooms.
              </p>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-lg border divide-y max-h-[50vh] overflow-y-auto">
        {mappings.map((m) => (
          <div
            key={m.detectedLabel}
            className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-3 py-2.5 bg-background"
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{m.detectedLabel}</p>
              <p className="text-xs text-muted-foreground">
                {m.photoCount} photo{m.photoCount !== 1 ? "s" : ""} on listing
              </p>
            </div>
            <select
              className="w-full sm:w-52 text-sm border rounded-md px-2 py-1.5 bg-background shrink-0"
              value={m.propertyRoomName ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                setMapping(m.detectedLabel, v === "__skip__" ? null : v);
              }}
              disabled={disabled}
            >
              <option value="__skip__">Don&apos;t import these photos</option>
              {propertyRoomNames.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>

      {mappings.every((m) => !m.propertyRoomName) && (
        <p className="text-xs text-destructive">Assign at least one listing room to continue.</p>
      )}
    </div>
  );
}
